import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { WorldManager } from '../core/WorldManager.js';
import { TerrainCollisionManager } from '../collision/TerrainCollisionManager.js';
import { TerrainMaterials } from './TerrainMaterials.js';
import { 
    WORLD_CHUNKS_ACROSS, 
    INITIAL_CHUNK_LOAD_RADIUS,
    TERRAIN_PHYSICS,
    CHUNK_SIZE,
    PLAYER_SPAWN,
    DEFAULT_TERRAIN_SETTINGS
} from './TerrainConstants.js';

/**
 * Initializes and manages all terrain-related systems
 * @param {THREE.Scene} scene - The Three.js scene
 * @param {Object} config - Configuration object for terrain settings
 * @returns {Object} Terrain management interface
 */
export function TerrainInitializer(scene, config = {}) {
    // Create managers
    const worldManager = new WorldManager(scene);
    const chunkManager = worldManager.chunkManager;
    
    // Get or create the singleton TerrainCollisionManager
    const terrainCollision = TerrainCollisionManager.getInstance();
    window.terrainCollision = terrainCollision; // Make it globally accessible

    // Initialize materials
    const terrainMaterials = new TerrainMaterials();
    
    // Calculate center coordinates
    const centerX = Math.floor(WORLD_CHUNKS_ACROSS / 2);
    const centerZ = Math.floor(WORLD_CHUNKS_ACROSS / 2);

    // Store player spawn globally for other systems to access
    window.playerSpawn = PLAYER_SPAWN;
    
    // Initialize debug systems if debug mode is enabled
    if (config.debug) {
        console.log("[TerrainInitializer] Debug mode enabled");
    }

    /**
     * Loads initial chunks around the center of the world
     * @returns {Promise<void>}
     */
    async function loadInitialChunks() {
        const radius = INITIAL_CHUNK_LOAD_RADIUS;
        const chunksToLoad = [];
        
        // First list all chunks we need to load
        for (let x = centerX - radius; x <= centerX + radius; x++) {
            for (let z = centerZ - radius; z <= centerZ + radius; z++) {
                // Only load chunks within a circular area
                const distSq = (x - centerX) * (x - centerX) + (z - centerZ) * (z - centerZ);
                if (distSq <= radius * radius) {
                    chunksToLoad.push({x, z});
                }
            }
        }
        
        // Load chunks in batches
        for (const {x, z} of chunksToLoad) {
            const chunk = chunkManager.getChunkAt(x, z);
            if (chunk?.mesh) {
                scene.add(chunk.mesh);
                chunkManager.loadedChunks.add(`${x},${z}`);
                console.log(`[TerrainInitializer] Loaded chunk at ${x},${z}`);
            }
        }
    }

    /**
     * Gets the terrain height at a given world position
     * @param {number} x - World X coordinate
     * @param {number} z - World Z coordinate
     * @returns {number} Terrain height
     */
    function getTerrainHeight(x, z) {
        return chunkManager.getHeightAt(x, z);
    }

    /**
     * Gets the terrain mesh for physics calculations
     * @returns {THREE.Mesh|null} The terrain mesh or null if not available
     */
    function getMeshForPhysics() {
        return chunkManager.getTerrainMesh();
    }

    /**
     * Verifies if a chunk is fully ready for use at a given world coordinate
     * @param {number} worldX - World X coordinate
     * @param {number} worldZ - World Z coordinate
     * @returns {boolean} True if the chunk is fully loaded and initialized
     */
    function isChunkReady(worldX, worldZ) {
        try {
            // 1. Verify chunk exists and is loaded
            const chunkX = Math.floor(worldX / CHUNK_SIZE);
            const chunkZ = Math.floor(worldZ / CHUNK_SIZE);
            const chunkKey = `${chunkX},${chunkZ}`;
            
            if (!chunkManager.loadedChunks.has(chunkKey)) {
                console.log(`Chunk ${chunkKey} not loaded`);
                return false;
            }

            const chunk = chunkManager.getChunkAt(chunkX, chunkZ);
            if (!chunk?.mesh) {
                console.log(`Chunk ${chunkKey} exists but has no mesh`);
                return false;
            }

            // 2. Verify heightMap has an entry
            const localX = ((worldX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const localZ = ((worldZ % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const height = chunk.getHeightAt(localX, localZ);
            
            if (height === undefined || height === null) {
                console.log(`No height data at ${worldX}:${worldZ}`);
                return false;
            }

            // 3. Verify TerrainCollisionManager has registered height
            const collisionHeight = terrainCollision.getCollisionHeight(worldX, worldZ);
            if (collisionHeight === terrainCollision.safetyFallbackHeight) {
                console.log(`Using fallback height at ${worldX}:${worldZ}`);
                return false;
            }

            // All checks passed
            console.log(`Chunk ready at ${worldX}:${worldZ} with height ${height}`);
            return true;

        } catch (error) {
            console.error(`Error checking chunk readiness at ${worldX}:${worldZ}:`, error);
            return false;
        }
    }

    // Initialize terrain
    loadInitialChunks();

    return {
        chunkManager,
        getTerrainHeight,
        getMeshForPhysics,
        terrainCollision,
        isChunkReady,
        terrainMaterials
    };
} 